import { parse as parsePhoneNumber } from '../../components/FieldPhoneNumberInput/e164Formatter';

export const getPartySizeTotal = partySize => {
  const adults = Number.isInteger(partySize?.adults) && partySize.adults > 0 ? partySize.adults : 0;
  const children =
    Number.isInteger(partySize?.children) && partySize.children > 0 ? partySize.children : 0;

  return adults + children;
};

const hasAtLeastOneGuest = answers => getPartySizeTotal(answers?.partySize) >= 1;

const NORMALIZED_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const NORMALIZED_TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
export const EVENT_TIME_INTERVAL_MINUTES = 30;
export const EVENT_TIME_OPTIONS = Array.from(
  { length: (24 * 60) / EVENT_TIME_INTERVAL_MINUTES },
  (_, index) => {
    const minutesFromMidnight = index * EVENT_TIME_INTERVAL_MINUTES;
    const hours = `${Math.floor(minutesFromMidnight / 60)}`.padStart(2, '0');
    const minutes = `${minutesFromMidnight % 60}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }
);
const selectableEventTimes = new Set(EVENT_TIME_OPTIONS);

export const getLocalDateValue = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDateFromLocalValue = value => {
  if (!NORMALIZED_DATE_PATTERN.test(value || '')) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
};

export const normalizeEventDate = value => {
  const date = value instanceof Date ? value : getDateFromLocalValue(value);
  return getLocalDateValue(date);
};

export const normalizeEventTime = value =>
  NORMALIZED_TIME_PATTERN.test(value || '') ? value : null;

export const normalizeEventTimeSelection = value =>
  selectableEventTimes.has(value) ? value : null;

export const getEventTimingValidation = (
  eventTiming = {},
  { minimumDate = getLocalDateValue(new Date()), maximumDate = null } = {}
) => {
  const timing = eventTiming || {};
  const date = normalizeEventDate(timing.date);
  const appetizerArrivalTime = normalizeEventTime(timing.appetizerArrivalTime);
  const mainServingTime = normalizeEventTime(timing.mainServingTime);
  const dateIsBeforeMinimum = !!date && !!minimumDate && date < minimumDate;
  const dateIsAfterMaximum = !!date && !!maximumDate && date > maximumDate;
  const timelineIsReversed =
    !!appetizerArrivalTime && !!mainServingTime && appetizerArrivalTime > mainServingTime;

  return {
    dateMissing: !date,
    dateIsBeforeMinimum,
    dateIsAfterMaximum,
    mainServingTimeMissing: !mainServingTime,
    timelineIsReversed,
    isComplete:
      !!date &&
      !dateIsBeforeMinimum &&
      !dateIsAfterMaximum &&
      !!mainServingTime &&
      !timelineIsReversed,
  };
};

const hasValidEventTiming = answers => getEventTimingValidation(answers?.eventTiming).isComplete;

export const KITCHEN_AVAILABILITY_OPTIONS = [
  { id: 'full-kitchen', label: 'Yes, full kitchen' },
  { id: 'limited-kitchen', label: 'Limited kitchen' },
  { id: 'no-kitchen', label: 'No kitchen' },
  { id: 'not-sure', label: 'Not sure' },
];

export const normalizeKitchenAvailability = value =>
  KITCHEN_AVAILABILITY_OPTIONS.some(option => option.id === value) ? value : null;

const hasKitchenAvailability = answers =>
  !!normalizeKitchenAvailability(answers?.kitchenAvailability);

export const normalizeVenueAddress = value => {
  const search =
    typeof value === 'string' ? value : typeof value?.search === 'string' ? value.search : '';
  const selectedPlace = value?.selectedPlace;
  const selectedAddress = typeof selectedPlace?.address === 'string' ? selectedPlace.address : null;

  return {
    search,
    selectedPlace: selectedAddress
      ? {
          address: selectedAddress,
          ...(selectedPlace.origin ? { origin: selectedPlace.origin } : {}),
        }
      : null,
  };
};

export const normalizeInquiryPhoneNumber = value =>
  parsePhoneNumber(typeof value === 'string' ? value.trim() : '');

export const isValidInquiryPhoneNumber = value => {
  const phoneNumber = normalizeInquiryPhoneNumber(value);
  const digitCount = (phoneNumber.match(/\d/g) || []).length;

  return phoneNumber.startsWith('+')
    ? /^\+[1-9]\d{7,14}$/.test(phoneNumber)
    : /^[\d\s().-]+$/.test(phoneNumber) && digitCount >= 7 && digitCount <= 15;
};

export const normalizeVenueAndContact = (value, prefill = {}) => {
  const answer = value && typeof value === 'object' ? value : {};
  const hasSavedFullName = Object.prototype.hasOwnProperty.call(answer, 'fullName');
  const hasSavedPhoneNumber = Object.prototype.hasOwnProperty.call(answer, 'phoneNumber');

  return {
    venueAddress: normalizeVenueAddress(answer.venueAddress),
    fullName: hasSavedFullName
      ? typeof answer.fullName === 'string'
        ? answer.fullName
        : ''
      : typeof prefill.fullName === 'string'
      ? prefill.fullName.trim()
      : '',
    phoneNumber: normalizeInquiryPhoneNumber(
      hasSavedPhoneNumber ? answer.phoneNumber : prefill.phoneNumber
    ),
  };
};

export const getVenueAndContactValidation = value => {
  const answer = normalizeVenueAndContact(value);
  const venueAddressMissing = !answer.venueAddress.search.trim();
  const fullNameMissing = !answer.fullName.trim();
  const phoneNumberMissing = !answer.phoneNumber;
  const phoneNumberInvalid = !phoneNumberMissing && !isValidInquiryPhoneNumber(answer.phoneNumber);

  return {
    venueAddressMissing,
    fullNameMissing,
    phoneNumberMissing,
    phoneNumberInvalid,
    isComplete:
      !venueAddressMissing && !fullNameMissing && !phoneNumberMissing && !phoneNumberInvalid,
  };
};

const hasValidVenueAndContact = answers =>
  getVenueAndContactValidation(answers?.venueAndContact).isComplete;

export const OCCASION_OPTIONS = [
  { id: 'wedding', label: 'Wedding' },
  { id: 'birthday', label: 'Birthday' },
  { id: 'anniversary', label: 'Anniversary' },
  { id: 'corporate-event', label: 'Corporate Event' },
  { id: 'dinner-party', label: 'Dinner Party' },
  { id: 'holiday', label: 'Holiday' },
  { id: 'other', label: 'Other' },
];

export const normalizeOccasionType = value =>
  OCCASION_OPTIONS.some(option => option.id === value) ? value : null;

const hasOccasionType = answers => !!normalizeOccasionType(answers?.occasionType);

export const BOOKING_INQUIRY_REVIEW_STEP = {
  id: 10,
  label: 'Review',
  routeName: 'BookingInquiryReviewPage',
};

export const BOOKING_INQUIRY_STEPS = [
  { id: 1, label: 'Allergies & Diet', routeName: 'BookingInquiryPage', isOptional: true },
  {
    id: 2,
    label: 'Favorite Foods',
    routeName: 'BookingInquiryFavoriteFoodsPage',
    isOptional: true,
  },
  {
    id: 3,
    label: 'Foods to Avoid',
    routeName: 'BookingInquiryFoodsToAvoidPage',
    isOptional: true,
  },
  {
    id: 4,
    label: 'Decor & Setup',
    routeName: 'BookingInquiryDecorSetupPage',
    isOptional: true,
  },
  {
    id: 5,
    label: 'Party Size',
    routeName: 'BookingInquiryPartySizePage',
    isOptional: false,
    isComplete: hasAtLeastOneGuest,
  },
  {
    id: 6,
    label: 'Date & Time',
    routeName: 'BookingInquiryEventTimingPage',
    isOptional: false,
    isComplete: hasValidEventTiming,
  },
  {
    id: 7,
    label: 'Kitchen',
    routeName: 'BookingInquiryKitchenAvailabilityPage',
    isOptional: false,
    isComplete: hasKitchenAvailability,
  },
  {
    id: 8,
    label: 'Venue',
    routeName: 'BookingInquiryVenueContactPage',
    isOptional: false,
    isComplete: hasValidVenueAndContact,
  },
  {
    id: 9,
    label: 'Occasion',
    routeName: 'BookingInquiryOccasionPage',
    isOptional: false,
    isComplete: hasOccasionType,
  },
];

export const NO_ALLERGIES_OPTION = 'no-allergies';

export const DIETARY_RESTRICTION_OPTIONS = [
  { id: NO_ALLERGIES_OPTION, label: 'No allergies' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'gluten-free', label: 'Gluten-free' },
  { id: 'dairy-free', label: 'Dairy-free' },
  { id: 'nut-allergy', label: 'Nut allergy' },
  { id: 'other', label: 'Other' },
];

export const FAVORITE_FOOD_OPTIONS = [
  { id: 'steak', label: 'Steak' },
  { id: 'seafood', label: 'Seafood' },
  { id: 'italian', label: 'Italian' },
  { id: 'french', label: 'French' },
  { id: 'japanese', label: 'Japanese' },
  { id: 'comfort-food', label: 'Comfort food' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'vegetarian', label: 'Vegetarian' },
];

export const NO_DECOR_SERVICES_OPTION = 'none';

export const DECOR_SERVICE_OPTIONS = [
  { id: 'linens', label: 'Linens' },
  { id: 'chargers-plates', label: 'Chargers / Plates' },
  { id: 'flatware', label: 'Flatware' },
  { id: 'glassware', label: 'Glassware' },
  { id: 'flowers', label: 'Flowers' },
  { id: 'full-table-setup', label: 'Full table setup' },
  { id: NO_DECOR_SERVICES_OPTION, label: 'None' },
  { id: 'other', label: 'Other' },
];

export const getNextInquiryStep = currentStep =>
  currentStep === BOOKING_INQUIRY_STEPS.length
    ? BOOKING_INQUIRY_REVIEW_STEP
    : BOOKING_INQUIRY_STEPS.find(step => step.id === currentStep + 1) || null;

export const getPreviousInquiryStep = currentStep =>
  BOOKING_INQUIRY_STEPS.find(step => step.id === currentStep - 1) || null;

export const isInquiryStepComplete = (stepId, answers = {}) => {
  const step = BOOKING_INQUIRY_STEPS.find(item => item.id === stepId);
  return step?.isComplete ? step.isComplete(answers) : true;
};

export const isBookingInquiryComplete = answers =>
  BOOKING_INQUIRY_STEPS.every(step => isInquiryStepComplete(step.id, answers));

export const toggleMultiSelectOption = (selectedOptions = [], optionId) =>
  selectedOptions.includes(optionId)
    ? selectedOptions.filter(option => option !== optionId)
    : [...selectedOptions, optionId];

export const toggleExclusiveOption = (selectedOptions = [], optionId, exclusiveOptionId) => {
  const currentlySelected = selectedOptions.includes(optionId);

  if (optionId === exclusiveOptionId) {
    return currentlySelected ? [] : [exclusiveOptionId];
  }

  const nonExclusiveOptions = selectedOptions.filter(option => option !== exclusiveOptionId);
  return currentlySelected
    ? nonExclusiveOptions.filter(option => option !== optionId)
    : [...nonExclusiveOptions, optionId];
};

export const toggleDietaryRestriction = (selectedOptions = [], optionId) => {
  return toggleExclusiveOption(selectedOptions, optionId, NO_ALLERGIES_OPTION);
};

export const toggleDecorServiceSelection = (selectedServices = [], serviceId) =>
  toggleExclusiveOption(selectedServices, serviceId, NO_DECOR_SERVICES_OPTION);
